import SelectTableFilter from 'components/atoms/select-table-filter'
import {
  SellerMarketProductStatusBadge,
  SellerMarketProductCurationStatusBadge
} from 'domains/seller-market-product/atoms'
import {
  PROCESS_STATUS,
  CURATION_STATUS,
  ACCEPTED_DATE,
  PRODUCTABLE_TYPE_LABEL,
  PRODUCTABLE_TYPE
} from 'domains/seller-market-product/constants'
import PropTypes from 'prop-types'
import TimeUtil from 'utils/time-util'
import StatusBadge from 'components/atoms/status-badge'
import { NavLink } from 'react-router-dom'
import { Button } from 'reactstrap'
import { SELLER_TYPE } from 'domains/seller/constants'

const propTypes = {
  rowButtonProps: PropTypes.arrayOf(
    PropTypes.shape({
      isDisabled : PropTypes.bool,
      colour     : PropTypes.string,
      onClick    : PropTypes.func,
      text       : PropTypes.string
    })
  )
}

const actionCellPropTypes = {
  cell: PropTypes.shape({
    row: PropTypes.shape({
      original: PropTypes.object
    })
  })
}

const filterPropTypes = {
  column: PropTypes.object
}

/**
 * Columns definition for Seller Market Product Table
 * @param {array} rowButtonProps - Array props for row button
 */
const sellerMarketProductTableColumns = ({
  rowButtonProps
}) => {

  // Custom filter
  const StatusFilter = ({ column }) => {
    const options = Object.keys(PROCESS_STATUS).map((key) => (
      {
        label : PROCESS_STATUS[key],
        value : PROCESS_STATUS[key]
      }
    ))
    return (
      <SelectTableFilter reactTableColumn={ column } options={ options } />
    )
  }

  StatusFilter.propTypes = filterPropTypes

  const CurationStatusFilter = ({ column }) => {
    const options = Object.keys(CURATION_STATUS).map((key) => (
      {
        label : CURATION_STATUS[key],
        value : CURATION_STATUS[key]
      }
    ))
    return (
      <SelectTableFilter reactTableColumn={ column } options={ options } />
    )
  }

  CurationStatusFilter.propTypes = filterPropTypes

  const AcceptedDateFilter = ({ column }) => {
    const options = Object.keys(ACCEPTED_DATE).map((key) => (
      {
        label : ACCEPTED_DATE[key].label,
        value : ACCEPTED_DATE[key].value
      }
    ))
    return (
      <SelectTableFilter reactTableColumn={ column } options={ options } enableAll={ false } />
    )
  }

  AcceptedDateFilter.propTypes = filterPropTypes

  const ProductableFilter = ({ column }) => {
    const options = Object.keys(PRODUCTABLE_TYPE).map((key) => (
      {
        label : PRODUCTABLE_TYPE_LABEL[PRODUCTABLE_TYPE[key]],
        value : PRODUCTABLE_TYPE[key]
      }
    ))
    return (
      <SelectTableFilter reactTableColumn={ column } options={ options } />
    )
  }

  ProductableFilter.propTypes = filterPropTypes

  // Custom cell
  // ** SELLER **
  const StatusCell = (row) => <SellerMarketProductStatusBadge status={ row.cell.value } />

  const CurationStatusCell = (row) => <SellerMarketProductCurationStatusBadge curation_status={ row.cell.value } />

  const generateActionCell = () => {

    const buttons = rowButtonProps.map((button) => {
      const ActionCell = ({ cell: { row } }) => {

        const buttonType    = button.type || 'NavLink'
        const isTypeNavlink = buttonType === 'NavLink'
        const isTypeButton  = buttonType ==='Button'

        if (isTypeNavlink) {
          return (
            <NavLink
              className={ `btn btn-${button.colour}` }
              to={ button.to(row.original) }
              data={ row.original }
              disabled={ button.isDisabled }
            >
              { button.text }
            </NavLink>
          )
        }

        if (isTypeButton){
          return (
            <Button
              className={ `btn btn-${button.colour}` }
              onClick={ () => button.onClick(row.original) }
              data={ row.original }
              disabled={ button.isDisabled }
            >
              { button.text }
            </Button>
          )
        }
      }

      ActionCell.propTypes = actionCellPropTypes

      return {
        Header : button.header,
        id     : button.id,
        Cell   : ActionCell
      }
    })

    return buttons
  }

  const ImageCell = (row) => (
    <img
      style={{ width: '5rem' }}
      src={ row.cell.value }
      alt={ row.row.original.sku }
    />
  )

  const LastNoteAtCell = (row) => {
    if (!row.cell.value) return '-'

    const today      = TimeUtil.now('DD MMM YY H:mm')
    const lastNoteAt = TimeUtil.format(row.cell.value, 'DD MMM YY H:mm')
    const diff       = TimeUtil.diff(today, lastNoteAt, 'day')
    // different in days
    const color = diff <= 1 ? 'success' : (diff < 7 ? 'warning' : 'danger')

    return <StatusBadge color={ color } label={ TimeUtil.format(row.cell.value, 'DD MMM YY H:mm') } />
  }
  const CreatedAtCell = (row) => {
    if (!row.cell.value) return '-'

    const today      = TimeUtil.now('DD MMM YY H:mm')
    const lastNoteAt = TimeUtil.format(row.cell.value, 'DD MMM YY H:mm')
    const diff       = TimeUtil.diff(today, lastNoteAt, 'day')
    // different in days
    const color = diff <= 1 ? 'success' : (diff < 7 ? 'warning' : 'danger')

    return <StatusBadge color={ color } label={ TimeUtil.format(row.cell.value, 'DD MMM YY H:mm') } />
  }

  const DateCell = (row) => TimeUtil.format(row.cell.value)

  const SellerCodeHeaderCell = () => (
    <div className='px-3' style={{ whiteSpace: 'pre-wrap' }}>
      { 'code' }
    </div>
  )
  const SellerTypeHeaderCell = () => (
    <div className='px-3' style={{ whiteSpace: 'pre-wrap' }}>
      { 'type' }
    </div>
  )

  const SellerTypeFilter = ({ column }) => {
    const options = Object.keys(SELLER_TYPE).map((key) => (
      {
        label : SELLER_TYPE[key],
        value : SELLER_TYPE[key]
      }
    ))
    return (
      <SelectTableFilter reactTableColumn={ column } options={ options } />
    )
  }
  SellerTypeFilter.propTypes = filterPropTypes

  const ProductableCell = (row) => {
    const sourceProductable = row.cell.value
    if (sourceProductable)
      return (
        <div className='px-3' style={{ whiteSpace: 'pre-wrap' }}>
          { PRODUCTABLE_TYPE_LABEL[sourceProductable.productable_type] }
        </div>
      )

    return (
      <div className='px-3' style={{ whiteSpace: 'pre-wrap' }}>
        { PRODUCTABLE_TYPE_LABEL[PRODUCTABLE_TYPE.MARKETPLACE] }
      </div>
    )
  }
  return (
    [
      {
        Header  : 'Seller Market Product',
        columns : [
          {
            Header         : 'Image',
            accessor       : 'thumbnail',
            Cell           : ImageCell,
            disableFilters : true
          },
          {
            Header   : 'SKU',
            accessor : 'sku'
          },
          {
            Header   : 'Name',
            accessor : 'name'
          },
          {
            Header   : 'Productable',
            accessor : 'source_productable',
            Cell     : ProductableCell,
            Filter   : ProductableFilter
          },
          {
            Header   : 'Process Status',
            accessor : 'process_status',
            Cell     : StatusCell,
            Filter   : StatusFilter
          },
          {
            Header   : 'Curation Status',
            accessor : 'curation_status',
            Cell     : CurationStatusCell,
            Filter   : CurationStatusFilter
          },
          {
            Header         : 'Created at',
            accessor       : 'created_at',
            Cell           : CreatedAtCell,
            disableFilters : true
          },
          {
            Header         : 'Last note at',
            accessor       : 'last_curation_note_at',
            Cell           : LastNoteAtCell,
            disableFilters : true
          },
          {
            Header   : 'Accepted Date',
            accessor : 'end_review_date',
            Cell     : DateCell,
            Filter   : AcceptedDateFilter
          },
          {
            Header   : 'Started\nBy',
            id       : 'curation_started_by_name',
            accessor : 'curation_started_by.name'
          },
          {
            Header   : 'Completed\nBy',
            id       : 'curation_completed_by_name',
            accessor : 'curation_completed_by.name'
          }
        ]
      },
      {
        Header  : 'Seller',
        columns : [
          {
            Header   : 'Name',
            id       : 'seller_name',
            accessor : 'seller.name'
          },
          {
            Header   : SellerTypeHeaderCell,
            id       : 'seller_type',
            accessor : 'seller.seller_type',
            Filter   : SellerTypeFilter
          },
          {
            Header   : SellerCodeHeaderCell,
            id       : 'seller_code',
            accessor : 'seller.code'
          }
        ]
      },
      {
        Header  : 'Actions',
        columns : [
          // * Generate action cell with buttonProps
          ...generateActionCell()
        ]
      }
    ]
  )
}

sellerMarketProductTableColumns.propTypes = propTypes

export default sellerMarketProductTableColumns
